// «Складской день» книги — единая логика для хранилища (/storage) и
// логистики (/log), чтобы день, под которым книга появляется, совпадал в обоих.
//
// Правило (часовой пояс производства UTC+5):
//  • до 10:00 — текущий день, с 10:00 — следующий день;
//  • если день попал на выходной — переносим на понедельник
//    (суббота → +2 дня, воскресенье → +1). Папок на выходные нет.
// Ручной override storeDate (если задан) имеет приоритет и минует это правило.

export const MONTHS_RU = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

// Часовой пояс производства — UTC+5 (Казахстан). Считаем строго в нём, чтобы
// результат не зависел от таймзоны устройства.
const UTC5_MS = 5 * 60 * 60 * 1000;

// «Складской день» (YYYY-MM-DD) из момента утверждения (book_archives.created_at).
export const storeDayKey = (iso: string): string => {
  // Стеночные часы UTC+5: сдвигаем инстант на +5ч и читаем UTC-поля.
  const w = new Date(new Date(iso).getTime() + UTC5_MS);
  // Полночь календарного дня UTC+5, выраженная как UTC-дата (для арифметики).
  const d = new Date(
    Date.UTC(w.getUTCFullYear(), w.getUTCMonth(), w.getUTCDate())
  );
  // Порог 10:00 — после него уезжаем на следующий день.
  if (w.getUTCHours() >= 10) d.setUTCDate(d.getUTCDate() + 1);
  // Перенос выходных на понедельник.
  const dow = d.getUTCDay(); // 0=вс … 6=сб
  if (dow === 6) d.setUTCDate(d.getUTCDate() + 2);
  else if (dow === 0) d.setUTCDate(d.getUTCDate() + 1);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// День-папка книги: ручной override storeDate в приоритете, иначе авто-расчёт.
export const effectiveDayKey = (a: {
  storeDate: string | null;
  createdAt: string;
}): string => a.storeDate || storeDayKey(a.createdAt);

// YYYY-MM-DD → DD.MM.YY для отображения.
export const dayKeyToLabel = (key: string): string => {
  const [yyyy, mm, dd] = key.split("-");
  return `${dd}.${mm}.${yyyy.slice(2)}`;
};

// Месяц/год по ключу YYYY-MM-DD.
export const dayKeyToMonthLabel = (key: string): string => {
  const [yyyy, mm] = key.split("-");
  const monthIdx = parseInt(mm, 10) - 1;
  return `${MONTHS_RU[monthIdx]} ${yyyy}`;
};

// Аббревиатура дня недели по ключу YYYY-MM-DD: пн/вт/…/вс.
export const WEEKDAYS_RU = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
export const weekdayAbbr = (key: string): string => {
  const [y, m, d] = key.split("-").map(Number);
  return WEEKDAYS_RU[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
};

// Сегодняшний календарный день в UTC+5 как ключ YYYY-MM-DD.
export const todayKeyUTC5 = (): string => {
  const w = new Date(Date.now() + UTC5_MS);
  const yyyy = w.getUTCFullYear();
  const mm = String(w.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(w.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Сдвиг ключа YYYY-MM-DD на N календарных дней (может быть отрицательным).
// Работает в UTC, поэтому DST/таймзонные эффекты не мешают.
export const addDayKey = (key: string, days: number): string => {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yyyy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(dt.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// Вчерашний календарный день в UTC+5 как ключ YYYY-MM-DD.
export const yesterdayKeyUTC5 = (): string => addDayKey(todayKeyUTC5(), -1);

// Сколько дней книга лежит в хранилище: от её складского дня до сегодня.
// Отрицательное (день в будущем — книгу перенесли вперёд) схлопываем в 0.
export const daysInStorage = (dayKey: string): number => {
  const [y, m, d] = dayKey.split("-").map(Number);
  const from = Date.UTC(y, m - 1, d);
  const [ty, tm, td] = todayKeyUTC5().split("-").map(Number);
  const to = Date.UTC(ty, tm - 1, td);
  return Math.max(0, Math.round((to - from) / 86400000));
};

// Порог «залежалась»: книга столько дней в хранилище и всё ещё не уехала
// в типографию. Статусы ниже — те, до которых печать ещё не случилась.
export const STALE_DAYS = 7;

const NOT_YET_PRINTED = new Set([
  "Пишет",
  "Заполнил",
  "Отредачено",
  "Готово под печать",
]);

// Книга «залежалась»: лежит дольше STALE_DAYS и до сих пор не отправлена в
// печать. Отложенные не считаем — их отложили осознанно.
export const isStale = (a: {
  storeDate: string | null;
  createdAt: string;
  postponed?: boolean;
  orderStatus: string | null;
}): boolean => {
  if (a.postponed) return false;
  if (!a.orderStatus || !NOT_YET_PRINTED.has(a.orderStatus)) return false;
  return daysInStorage(effectiveDayKey(a)) > STALE_DAYS;
};

// Если ключ YYYY-MM-DD попал на выходной — переносим на понедельник
// (суббота → +2 дня, воскресенье → +1). Будни возвращаем как есть.
export const shiftWeekendToMonday = (key: string): string => {
  const [y, m, d] = key.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=вс … 6=сб
  if (dow === 6) return addDayKey(key, 2);
  if (dow === 0) return addDayKey(key, 1);
  return key;
};
